// Sales > Targeting (the Territory tab): typed routes. Verified against
// src/financial/router.ts (/api/financial: the pilot-priority board and the reimbursement
// opportunity of a metro; no honesty flag, the opportunity rows carry `modeled: true`) and
// src/aithora/v3/sales.ts (/api/aithora: per-rep warmth, snooze, EHR). Logging a call,
// starting a deal, searching the catalog and adding a note use the CRM routes in
// sales-routes.ts (crmFacilityCall, crmDealCreate, crmDeals, crmFacilitySearch,
// crmActivityCreate).
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  TgEhrResult,
  TgNetwork,
  TgOpportunityQuery,
  TgOpportunityResponse,
  TgSnoozeResult,
  TgTargetingQuery,
  TgTargetingResponse,
  TgTerritory,
  TgWarmthResult,
} from './sales-targeting-types';

const A = AITHORA_PREFIX;
const F = '/api/financial';

/** GET /facilities/targeting?metro=&tier=&type=&sort=&limit<=200&offset=: the metro's facilities scored by the pilot-priority engine. */
export const tgTargeting = route<TgTargetingResponse, TgTargetingQuery>()('GET', `${F}/facilities/targeting`);
/** GET /facilities/reimbursement-opportunity?metro=&type=&sort=opportunity&order=&limit<=500: modeled open dollars per facility, and the rollup. */
export const tgOpportunity = route<TgOpportunityResponse, TgOpportunityQuery>()('GET', `${F}/facilities/reimbursement-opportunity`);

/** One code line of a facility's modeled opportunity (financial/reimbursement-opportunity.ts). */
export interface TgOpportunityCode {
  code: string;
  category: string;
  label: string;
  rate: number;
  frequency: 'monthly' | 'annual';
  rateVerified: boolean;
  eligibleResidents: number;
  /** Annual $ at the stated capture rate (the national uptake unless a scenario sets it). */
  annual: number;
  /** Share of eligible residents assumed billed. */
  captureRate?: number;
  /** Annual $ if every eligible resident were billed: a ceiling, never the headline. */
  atFullCapture?: number;
  eligibilitySource?: string;
  primary?: boolean;
  gate: string;
}
/** GET /facilities/:id/reimbursement-opportunity: one facility's code stack and state pool (restored from production). */
export interface TgFacilityOpportunity {
  modeled: true;
  facilityId: string | null;
  name: string | null;
  state: string | null;
  type: string | null;
  residents: number;
  censusEstimated: boolean;
  medicareEligible: number;
  codes: TgOpportunityCode[];
  /** Part B dollars a year at the national billing rate: context in the breakdown. */
  reimbursementAnnual: number;
  /** Every eligible resident billed every period: the headline. */
  atFullCaptureAnnual?: number;
  /** The disclaimer shown on hover over the headline. */
  headlineNote?: string;
  capture?: { basis: 'national_uptake' | 'scenario'; source: string };
  /** Possible program money, up to: shown apart, never part of opportunityAnnual. */
  statePool: { state: string; pool: string; annualUpTo: number; note: string; label?: string } | null;
  /** The facility's headline figure: full capture. State pools are never in it. */
  opportunityAnnual: number;
  perResidentPerYear: number;
  notEstimated?: Array<{ code: string; why: string }>;
  caveats: string[];
}
/**
 * The disclaimer on the opportunity headline, for every role (the back end's
 * OPPORTUNITY_HEADLINE_NOTE carries the same words; a test holds them equal).
 */
export const OPPORTUNITY_HEADLINE_NOTE =
  'Assumes every eligible resident is billed every month. Nationally only about 3% are (CMS 2024). '
  + 'Eligibility from CDC NPALS 2022 and CMS; rates from Medicare 2026. Includes the state quality program, up to its published amount, where the building qualifies.';
export const tgFacilityOpportunity = route<TgFacilityOpportunity>()('GET', `${F}/facilities/:id/reimbursement-opportunity`);
/** GET /reimbursement-catalog: every program the engine models, and the state pool ceilings. */
export interface TgProgramCatalog {
  codes: Array<{ code: string; label: string; category: string; rate?: number; frequency?: string; rateVerified?: boolean; [k: string]: unknown }>;
  statePools: Array<{ state: string; pool: string; size?: string; annualUpTo: number }>;
  updated: string;
  note: string;
}
export const tgProgramCatalog = route<TgProgramCatalog>()('GET', `${F}/reimbursement-catalog`);

/** GET /crm/facilities/:facilityId/territory: my warmth and snooze, the team warmth, the EHR. */
export const tgTerritory = route<TgTerritory>()('GET', `${A}/crm/facilities/:facilityId/territory`, { honest: true });
/** POST /crm/facilities/:facilityId/warmth {warmth 1-5, note?}: saved for this rep and mirrored to the team row. */
export const tgWarmth = route<TgWarmthResult, void, { warmth: number; note?: string }>()('POST', `${A}/crm/facilities/:facilityId/warmth`, { honest: true });
/** POST /crm/facilities/:facilityId/snooze {days}: 7 hides it from my Signals for a week; 0 clears. */
export const tgSnooze = route<TgSnoozeResult, void, { days: number }>()('POST', `${A}/crm/facilities/:facilityId/snooze`, { honest: true });
/** POST /crm/facilities/:facilityId/ehr {ehr}: the discovery-captured EHR (null clears). */
export const tgEhr = route<TgEhrResult, void, { ehr: string | null }>()('POST', `${A}/crm/facilities/:facilityId/ehr`, { honest: true });

/** GET /crm/facilities/:facilityId/network: payer mix and nearest hospitals (real: false, empty, until the data exists). 404 = not in the catalog. */
export const tgNetwork = route<TgNetwork | { error?: string }>()('GET', `${A}/crm/facilities/:facilityId/network`, { honest: true, acceptStatuses: [404] });
